import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePremium } from '../../hooks/usePremium';
import { isNativeApp } from '../../lib/platform';

/**
 * PremiumGate — wraps a feature. If user is premium/trial/admin, renders children.
 * Otherwise shows a soft lock overlay with upgrade CTA.
 *
 * Two modes:
 *   <PremiumGate>             ← overlay mode (children visible but dimmed)
 *   <PremiumGate replace>      ← replacement mode (children hidden entirely)
 *
 * Prop `onLockedClick` can override the default /upgrade navigation.
 */
export function PremiumGate({ children, replace = false, feature, label, className, onLockedClick }) {
  const { loading, isPremium } = usePremium();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className={cn('inline-flex items-center justify-center opacity-50', className)}>
        {children}
      </div>
    );
  }
  if (isPremium) return <>{children}</>;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onLockedClick) onLockedClick();
    else navigate(`/upgrade?feature=${encodeURIComponent(feature || label || 'premium')}`);
  };

  if (replace) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3',
          'bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400',
          'text-white font-bold text-sm shadow-md transition-all',
          className
        )}
        data-testid={`premium-gate-${feature || label || 'cta'}`}
      >
        <Sparkles size={16} />
        {isNativeApp() ? (label || 'Premium Feature') : `Unlock ${label || 'Premium Feature'}`}
        <span className="ml-1 text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-bold">PREMIUM</span>
      </button>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}
      className={cn(
        'relative group cursor-pointer',
        className
      )}
      data-testid={`premium-gate-${feature || label || 'overlay'}`}
    >
      <div className="pointer-events-none select-none opacity-40 blur-[0.5px]">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px] rounded-lg group-hover:bg-black/40 transition-colors">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/90 text-white text-[11px] font-bold shadow-md">
          <Lock size={11} />
          {label || 'Premium'}
        </span>
      </div>
    </div>
  );
}

/**
 * PremiumBadge — small inline "PREMIUM" pill for feature labels.
 */
export function PremiumBadge({ className }) {
  return (
    <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px] font-extrabold tracking-wider', className)}>
      <Sparkles size={9} /> PREMIUM
    </span>
  );
}
